import type { IReduxState } from 'frontend/configureStore';
import { Alert, Box, Button, FileTypeIcon, Text } from '@ksyos/design-system';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { ConclusieDiagnoseOption } from '../../../../../common/enums/ConclusieDiagnoseOption';
import { getDossierUrl } from '../../../../services/api/dossierService';
import { CollapseCard } from './CollapseCard';

type ResultCardProps = Readonly<{
    isInitiallyOpen?: boolean;
    result: ConclusieDiagnoseOption;
}>;

export function ResultCard({ isInitiallyOpen, result }: ResultCardProps) {
    const [isLoadingDossier, setIsLoadingDossier] = useState(false);
    const [downloadError, setDownloadError] = useState(false);
    const patientName = useSelector((state: IReduxState) => state.authentication.user?.displayName);

    return (
        <CollapseCard
            isInitiallyOpen={isInitiallyOpen}
            title="Uitslag"
        >
            <>
                {result === ConclusieDiagnoseOption.geen_afwijkingen && (
                    <Text mb={3}>De resultaten van het onderzoek zijn goed.</Text>
                )}
                <Text mb={3}>
                    Hieronder vind je de medische gegevens die bekend zijn bij Ksyos. Download alle informatie in een
                    PDF bestand.
                </Text>
                <Text mb={3}>Je huisarts en optometrist ontvangen ook de uitslag van het onderzoek.</Text>
                <Box
                    alignItems="center"
                    display="flex"
                    flexDirection="column"
                    marginTop={3}
                    paddingY={3}
                >
                    <div data-dd-action-name="File type icon">
                        <FileTypeIcon
                            icon="pdf"
                            onClick={() => downloadDossier()}
                            width={44}
                        />
                    </div>
                    <Text
                        fontStyle="italic"
                        marginTop={4}
                        size="sm"
                    >
                        Medische gegevens {patientName ?? ''}
                    </Text>
                </Box>
            </>
            <div data-dd-action-name="Download button">
                <Button
                    color="secondary"
                    isLoading={isLoadingDossier}
                    onClick={() => downloadDossier()}
                    variant="solid"
                    width="full"
                >
                    Download gegevens
                </Button>
            </div>
            {downloadError && (
                <Alert appearance="danger">
                    Er is een fout opgetreden bij het downloaden van het dossier. Probeer het later nog eens.
                </Alert>
            )}
        </CollapseCard>
    );

    async function downloadDossier() {
        if (isLoadingDossier) {
            return;
        }

        setDownloadError(false);
        setIsLoadingDossier(true);
        try {
            const dossierUrl = await getDossierUrl();
            window.open(dossierUrl, '_blank');
        } catch (error) {
            console.error('Failed to download dossier', error);
            setDownloadError(true);
        } finally {
            setIsLoadingDossier(false);
        }
    }
}
